"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "lucide-react";

// 🔹 Root
function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root {...props} />;
}

// 🔹 Group
function SelectGroup(props: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group {...props} />;
}

// 🔹 Value
function SelectValue(props: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value {...props} />;
}

// 🔹 Trigger (Main Select Button)
function SelectTrigger({
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  const height = size === "sm" ? "2rem" : "2.25rem";

  const baseStyle: React.CSSProperties = {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    backgroundColor: "#fff",
    color: "#111827",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    whiteSpace: "nowrap",
    transition: "all 0.2s ease",
    outline: "none",
    height,
    cursor: "pointer",
  };

  return (
    <SelectPrimitive.Trigger
      {...props}
      style={baseStyle}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#2563eb";
        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.3)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "#d1d5db";
        e.currentTarget.style.boxShadow = "none";
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon style={{ width: 16, height: 16, opacity: 0.5 }} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

// 🔹 Content (Dropdown Menu)
function SelectContent({
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        {...props}
        position={position}
        style={{
          background: "#fff",
          color: "#111827",
          position: "relative",
          zIndex: 10200,
          maxHeight: "var(--radix-select-content-available-height)",
          minWidth: "8rem",
          overflowX: "hidden",
          overflowY: "auto",
          borderRadius: "6px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          transformOrigin: "var(--radix-select-content-transform-origin)",
          animation: "fadeIn 0.15s ease, zoomIn 0.15s ease",
        }}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          style={{
            padding: "0.25rem",
            width: "100%",
            minWidth: "var(--radix-select-trigger-width)",
          }}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

// 🔹 Label
function SelectLabel(props: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      {...props}
      style={{
        color: "#6b7280",
        padding: "0.375rem 0.5rem",
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    />
  );
}

// 🔹 Item
function SelectItem({
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const baseStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    width: "100%",
    borderRadius: "4px",
    padding: "0.375rem 2rem 0.375rem 0.5rem",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    transition: "background-color 0.2s ease, color 0.2s ease",
  };

  return (
    <SelectPrimitive.Item
      {...props}
      style={baseStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f3f4f6";
        e.currentTarget.style.color = "#111827";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.color = "#111827";
      }}
    >
      <span
        style={{
          position: "absolute",
          right: "0.5rem",
          display: "flex",
          width: "1rem",
          height: "1rem",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon style={{ width: 16, height: 16 }} />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

// 🔹 Separator
function SelectSeparator(
  props: React.ComponentProps<typeof SelectPrimitive.Separator>
) {
  return (
    <SelectPrimitive.Separator
      {...props}
      style={{
        backgroundColor: "#e5e7eb",
        pointerEvents: "none",
        margin: "0.25rem 0",
        height: "1px",
      }}
    />
  );
}

// 🔹 Scroll Buttons
function SelectScrollUpButton(
  props: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
) {
  return (
    <SelectPrimitive.ScrollUpButton
      {...props}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.25rem",
        cursor: "default",
      }}
    >
      <ChevronUpIcon style={{ width: 16, height: 16 }} />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton(
  props: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>
) {
  return (
    <SelectPrimitive.ScrollDownButton
      {...props}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.25rem",
        cursor: "default",
      }}
    >
      <ChevronDownIcon style={{ width: 16, height: 16 }} />
    </SelectPrimitive.ScrollDownButton>
  );
}

// 🔹 Exports
export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
